import { Link, router } from "@inertiajs/react"
import { BadgeCheck, Settings2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { CandidateReviewCard } from "@/components/candidates/candidate-review-card"
import { EmptyState } from "@/components/empty-state"
import { HomeTriageSection } from "@/components/home-triage/home-triage-section"
import { Button } from "@/components/ui/button"
import { brandSettingsCandidatesPath } from "@/routes"
import type { CandidatesToReviewSection as SectionData } from "@/types/home-triage"

/**
 * OKS-950 — Brand/Home § "En attente de votre validation".
 *
 * The brand manager's own decision queue: candidates the agency has sent for
 * sign-off. Built on the shipped {@link HomeTriageSection} shell and the
 * shipped {@link CandidateReviewCard} — no new shell, no forked card.
 *
 * **Why this is a separate section and not a widened "Candidates to review".**
 * That queue is the AGENCY's work in progress. A brand manager shown rows the
 * agency has not finished triaging would be deciding on candidates nobody has
 * screened yet — and the server enforces the split by passing a different
 * `statuses:` argument rather than by widening a shared constant, so the two
 * segments cannot bleed into each other (PRD AC4).
 *
 * **Named by who is waiting, not by the verb.** « En attente de votre
 * validation » sits on the same page as the shipped « Candidat·e·s à
 * examiner »; « Candidat·e·s à valider » would have differed from it by one
 * word at a glance (OQ9).
 *
 * **Cold start points at the setting.** A brand that has never had a candidate
 * sent for validation may simply be routed `never` — so the empty body offers
 * the settings page rather than implying something is broken. Distinct from
 * `empty`, which means "you're caught up".
 */
export interface CandidatesAwaitingValidationSectionProps {
  section: SectionData
}

export function CandidatesAwaitingValidationSection({
  section,
}: CandidatesAwaitingValidationSectionProps) {
  const { t } = useTranslation()
  const [isRetrying, setIsRetrying] = useState(false)

  function handleRetry() {
    setIsRetrying(true)
    router.reload({
      only: ["candidates_awaiting_validation"],
      onFinish: () => setIsRetrying(false),
    })
  }

  const state = isRetrying ? "loading" : section.state

  return (
    <HomeTriageSection
      title={t("candidates.awaiting_validation.title")}
      state={state}
      count={section.count}
      viewAllUrl={section.view_all_url}
      onRetry={handleRetry}
      isRetrying={isRetrying}
      testId="candidates-awaiting-validation"
      // OKS-953 (PRD R7) — one of the FIVE candidate segments that collapse.
      collapseWhenEmpty
      emptyTestId="candidates-awaiting-validation-empty"
      renderColdStart={() => (
        <EmptyState
          icon={Settings2}
          title={t("candidates.awaiting_validation.cold_start.title")}
          titleAs="h2"
          description={t(
            "candidates.awaiting_validation.cold_start.description",
          )}
          data-testid="candidates-awaiting-validation-cold-start"
          actions={
            <Button asChild variant="outline">
              <Link href={brandSettingsCandidatesPath()}>
                {t("candidates.awaiting_validation.cold_start.cta")}
              </Link>
            </Button>
          }
        />
      )}
      renderEmpty={() => (
        <EmptyState
          icon={BadgeCheck}
          title={t("candidates.awaiting_validation.empty.title")}
          titleAs="h2"
          description={t("candidates.awaiting_validation.empty.description")}
          data-testid="candidates-awaiting-validation-empty"
        />
      )}
    >
      {section.items.map((item) => (
        <CandidateReviewCard key={item.id} item={item} />
      ))}
    </HomeTriageSection>
  )
}
